import { useMemo } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { UsersRound } from 'lucide-react';
import { createSearchParams, useNavigate } from 'react-router';
import { useSuspenseQuery } from '@apollo/client/react';

import { EscrowMemberRoleEnum, EscrowMembersOrderByEnum, QueryOrderEnum } from 'src/graphql/schema';
import { EscrowMembersDocument } from 'src/graphql/actions/escrowMembers/escrowMembers.query';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { roleOptionsDict } from 'src/containers/Escrow/Members';
import { cn } from 'src/lib/utils';
import { Button } from '@/components/ui/button';
import { withSuspense } from 'src/hoc';
import { getMemberSortPriority } from 'src/containers/Backup/Members/Management';

type IMemberRow = {
  id: string;
  member: {
    name: string;
    email: string;
    canUpdate: boolean;
  };
  role: EscrowMemberRoleEnum;
};

const columnHelper = createColumnHelper<IMemberRow>();
const columns = [
  columnHelper.accessor('member', {
    cell: (props) => {
      const { name, email } = props.getValue();
      const fallback = name?.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-8 md:size-10 rounded-full">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <span className="text-sm text-foreground font-medium line-clamp-1">{name}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('role', {
    cell: (props) => roleOptionsDict[props.getValue()],
  }),
];

const MembersTable = withSuspense(({ escrowId, escrowPartyId, basePath }: Props) => {
  const navigate = useNavigate();
  const {
    data: { escrowMembers },
  } = useSuspenseQuery(EscrowMembersDocument, {
    variables: {
      escrowPartyId,
      page: 1,
      perPage: 100,
      orderBy: EscrowMembersOrderByEnum.Role,
      order: QueryOrderEnum.Asc,
    },
  });
  const {
    data: {
      profile: { id: profileId },
    },
  } = useSuspenseQuery(ProfileDocument);
  const membersMapped = useMemo(() => {
    const sortedNodes = escrowMembers.nodes.slice().sort((a, b) => {
      const priorityA = getMemberSortPriority(a.user, profileId);
      const priorityB = getMemberSortPriority(b.user, profileId);

      if (priorityA !== priorityB) return priorityA - priorityB;

      return 0;
    });

    return sortedNodes.map((member) => ({
      id: member.id,
      member: {
        name: member.user.name || '-',
        email: member.user.email || '-',
        canUpdate: member.policy.update,
      },
      role: member.role,
    }));
  }, [escrowMembers.nodes, profileId]);
  const table = useReactTable({
    data: membersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const onEdit = () =>
    navigate({
      pathname: `${basePath}/onboarding/${escrowId}/members`,
      search: createSearchParams({
        isEdit: 'true',
      }).toString(),
    });

  return (
    <div className="overflow-hidden rounded-sm border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Team member</TableHead>
            <TableHead className="w-[170px]">Permissions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} className={cn(!row.original.member.canUpdate && 'bg-slate-100 hover:bg-slate-100')}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={2}>
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={onEdit}>
                  Edit
                </Button>
              </div>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
});

type Props = {
  escrowId: string;
  escrowPartyId: string;
  basePath: string;
};

const MembersCard = ({ escrowId, escrowPartyId, basePath }: Props) => {
  return (
    <Card>
      <CardHeader className="relative">
        <CardTitle className="flex items-center gap-2 text-xl">
          <UsersRound className="size-6" />
          Escrow members
        </CardTitle>
        <CardDescription>Team members for this specific escrow and their assigned permissions.</CardDescription>
      </CardHeader>
      <CardContent>
        <MembersTable escrowId={escrowId} escrowPartyId={escrowPartyId} basePath={basePath} />
      </CardContent>
    </Card>
  );
};

export default MembersCard;
